import React, { useState } from 'react';
import { Splitter, SplitterOnChangeEvent } from '@progress/kendo-react-layout';
import InputSection from './input-section';
import OutputSection from './output-section';
import ProjectSummary from '../common/Jetlift/project-summary';
import { LABELS } from '../../constants/labels/jetlift/jetlift.labels';
import ProjectHeader from '../common/Jetlift/project-header';
import { useForm } from 'react-hook-form';
import { useAppSelector, useAppDispatch } from '../../store/hooks/StoreHooks';
import CaseComparison from './comparison';
import Reports from './reports';
import useJetliftGasCalculationFacade from '../../facade/jetlift/jetlift-output/gasCalculation.facade';
import { setGasCalculationComplete } from '../../store/jetlift/jetlift-output/GasCalculationSlice';
import Notification from '../common/notification';

interface JetliftProps {
    children?: React.ReactNode;
    className?: string;
}

const Jetlift: React.FC<JetliftProps> = ({ children, className }) => {
    const { isComparison, isReports } = useAppSelector((state) => state?.jetlift?.projectHeader);
    const { fluidInput } = useAppSelector((state) => state?.jetlift?.fluid);
    const { wellInput } = useAppSelector((state) => state?.jetlift?.well);
    const { jetPump } = useAppSelector((state) => state?.jetlift?.jetPump);
    const { gasIntakeData } = useAppSelector((state) => state?.jetlift?.gasCalculations);
    const { basicSummary } = useAppSelector((state) => state?.jetlift?.basicCalculations);
    const { GasCalculationStart } = useJetliftGasCalculationFacade();
    const [notifications, setNotifications] = useState<string[]>([]);
    const dispatch = useAppDispatch();
    const [horizontalPanes, setHorizontalPanes] = useState<Array<any>>([
        { size: "30%", collapsible: true },
        {},
        { size: "70%", collapsible: true },
    ]);

    const onHorizontalChange = (event: SplitterOnChangeEvent) => {
        setHorizontalPanes(event.newState);
    };

    const methods = useForm({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {},
        shouldUnregister: false,
    });

    const handleValidateAll = async () => {
        const isValid = await methods.trigger(undefined, { shouldFocus: true });
        if (!isValid) {
            const errors = methods.formState.errors as Record<string, any>;
            Object.entries(errors).forEach(([name, err]) => {
                const msg = 'is required.';
                let allValidationMessages: string[] = [];
                allValidationMessages?.push(`${name} ${msg}`);
                setNotifications((prevNotifications) => [
                    ...prevNotifications,
                    ...allValidationMessages,
                ]);
            });
        } else {
            const gasAvailability = fluidInput?.gasAvailblty;
            if (gasAvailability === "1") {
                GasCalculationStart();
            } else {
                dispatch(setGasCalculationComplete(true));
            }
        }
    };

    const removeNotification = (index: number) => {
        setNotifications((prevNotifications) =>
            prevNotifications.filter((_, i) => i !== index)
        );
    };

    const summaryData = [
        { label: "Nozzle", value: jetPump?.nozzleDiameterSize ?? "", unit: "" },
        { label: "Target rate", value: wellInput?.productionRate || "", unit: "blpd" },
        { label: "Injection pressure to achieve target rate", value: basicSummary?.surfacePowerFluidInjectionPressure !== "NaN" ? Number(basicSummary?.surfacePowerFluidInjectionPressure)?.toFixed(2) : "", unit: "psia" },
        { label: "Surface pump size", value: jetPump?.selected_surface_pump_size ?? "", unit: "" },
        { label: "% of maximum pump speed", value: 70, unit: "% target prod. rate", isSuccess: true },
        { label: "Recommended vessel size", value: jetPump?.recommended_vessel_size ?? "", unit: "km" },
        { label: "Throat", value: jetPump?.throatDiameterSize ?? "", unit: "" },
        { label: "PIP", value: gasIntakeData?.pip || "", unit: "psia" },
        { label: "Injection rate to achieve target rate", value: basicSummary?.surfacePowerFluidInjectionRate !== "NaN" ? Number(basicSummary?.surfacePowerFluidInjectionRate)?.toFixed(2) : "", unit: "blpd" },
        { label: "Surface pump plunger size", value: jetPump?.selected_plunget_size ?? "", unit: "" },
        { label: "% of maximum pump speed", value: 90, unit: "% highest inj. rate", isWarning: true },
    ];

    return (
        <div className={className ? className : "flex flex-col gap-[16px] w-full"}>
            {children}
            <ProjectHeader onCalculate={handleValidateAll} />
            {!isComparison && !isReports && (
                <>
                    <ProjectSummary data={summaryData} title={LABELS?.projectSummary} />
                    <Splitter
                        style={{ minHeight: "400px" }}
                        panes={horizontalPanes}
                        onChange={onHorizontalChange}
                    >
                        <InputSection methods={methods} />
                        <OutputSection />
                    </Splitter>
                </>
            )}
            {isComparison && !isReports && (
                <div><CaseComparison /></div>
            )}
            {isReports && !isComparison && (
                <div><Reports /></div>
            )}
            <div className='fixed top-4 right-4 z-[1005] flex flex-col space-y-4'>
                {notifications.map((message, index) => (
                    <Notification
                        key={index}
                        type='error'
                        message={message}
                        onClose={() => removeNotification(index)}
                        isOpen={true}
                        timeout={2000}
                    />
                ))}
            </div>
        </div>
    );
};

export default Jetlift;